<template>
  <div class="page_content">
    <job-header
      :uuid="uuid"
      :embedded="embedded"
      :show-apply="isStudentLogin"
      :show-recommend="isAdvisorLogin"
      :has-application="hasApplication"
      :applied-message="appliedMessage"
      @apply="showApplyPopup"
      @recommend="showRecommendPopup">
    </job-header>
    <b-container fluid>
      <b-row class="page-content-section">
        <div class="left-container">
          <div class="job-summary" :class="{ 'is-embedded': embedded }">

          <div class="job-meta-row app-status  ace_sec_txt" v-if="hasApplication">
            <div class="job-meta-item job-description-item">
              <div class="icon_container">
                <span class="ic icon-paragraph-left"></span>
              </div>
              <div>
                <h1 class="head_txt module_type_name ace_sec_hd" role="heading" aria-level="1">
                  {{ $t('applicationStatus') }} :
                </h1>
              </div>
            </div>
          </div>
          <div class="job-meta-row description_row ace_sec_txt" v-if="hasApplication">
            <div class="job-meta-item job-description-item">
              <div class="job-application-status" :class="{ 'is-embedded': embedded }">
                <template v-for="(stage, stageIndex) in statusStages">
                  <div
                    class="job-status-box"
                    :key="'detail-' + stage.key"
                    :class="{
                      'is-complete': isStageComplete(userApplication, stage),
                      'is-rejected': isStageRejected(userApplication, stage),
                      'has-offer-actions': canRespondToOffer(userApplication, stage)
                    }">
                    <template v-if="canRespondToOffer(userApplication, stage)">
                      <div class="offer-actions">
                        <button type="button" class="offer-btn accept" @click.stop="confirmOfferResponse('accept')">{{ $t('accept') }}</button>
                        <button type="button" class="offer-btn decline" @click.stop="confirmOfferResponse('decline')">{{ $t('decline') }}</button>
                      </div>
                    </template>
                    <template v-else>
                      <span class="job-status-label">{{ stageDisplayLabel(userApplication, stage) }}</span>
                      <span class="job-status-date">{{ stageDateLabel(userApplication, stage) }}</span>
                    </template>
                  </div>
                  <span
                    class="job-status-separator"
                    v-if="stageIndex < statusStages.length - 1"
                    :key="'sep-' + stage.key"
                    aria-hidden="true">&gt;</span>
                </template>
              </div>
            </div>
          </div>

          <div class="job-summary-header">
            <div class="job-summary-meta">
              <div class="job-meta-row app-status ace_sec_txt recommendation-row" v-if="hasRecommendation">
                <div class="job-meta-item job-description-item">
                  <div
                    class="recommendation-item"
                    v-for="(recommendation, index) in userRecommendations"
                    :key="recommendation.uuid || (recommendation.recommendedBy + '-' + recommendation.type + '-' + index)">
                    <div class="icon_container recommendation-star">
                      <span class="ic icon-star-full"></span>
                    </div>
                    <div>
                      {{ $t('thisJobRecommendedToYouBy') }} : {{ recommendation.recommendedBy }} ({{ recommendation.type }})
                    </div>
                  </div>
                </div>
              </div>
              <div class="job-meta-row job-meta-align">
                <div class="job-meta-item job-salary-item">
                  <div class="icon_container">
                    <span class="ic_salary"></span>
                  </div>
                  <div class="ace_sec_txt" :title="job.salaryRange || $t('na')">
                    {{ job.salaryRange || $t('na') }}
                  </div>
                </div>
                <div class="job-meta-item job-type-item">
                  <div class="icon_container">
                    <span class="ic2_part-full-time-job"></span>
                  </div>
                  <div class="ace_sec_txt">
                    {{ formatJobTypeLabel(jobTypeLabel) }}
                  </div>
                </div>
                <div class="job-meta-item" v-if="!embedded">
                  <div class="icon_container">
                    <span class="icon-location"></span>
                  </div>
                  <div class="ace_sec_txt">
                    {{ jobLocation }}
                  </div>
                </div>
              </div>
              <div class="job-meta-row job-meta-align">
                <div class="job-meta-item">
                  <div class="icon_container">
                    <span class="ic2_calendar"></span>
                  </div>
                  <div class="ace_sec_txt">
                    {{ $t('postedOn') }} {{ millisToDate(job.postedDate || job.creationTime) }}
                  </div>
                </div>
                <div class="job-meta-item">
                  <div class="icon_container">
                    <span class="ic2_applyBy"></span>
                  </div>
                  <div class="ace_sec_txt">
                    {{ $t('applyBy') }} {{ millisToDate(job.applyBy) }}
                  </div>
                </div>
                <div class="job-meta-item job-tags-item" v-if="!embedded">
                  <div class="icon_container">
                    <span class="ic22-tag2"></span>
                  </div>
                  <div class="details ace_sec_txt">
                    <template v-if="hasJobTags">
                      <div class="tags_container">
                        <div class="tags_cat">
                          <div class="tag" v-for="item in visibleJobTags" :key="item.key" :class="item.cssClass" :aria-label="item.name" tabindex="0" role="button" @click.stop="onTagClick(item)" @keydown.enter.prevent="onTagClick(item)">
                            <span  class="tag_name" :class="item.nameClass" :title="item.name">{{item.name}}</span>
                            <span class="tag_lavel" v-if="item.level">{{item.level}}</span>
                          </div>
                        </div>
                      </div>
                      <b-button class="btn ic_btn btn_ellipsis"
                        v-if="hasMoreJobTags"
                        @click="showTagsPopup"
                        :aria-label="$t('showAllTags')"
                        :title="$t('showAllTags')"
                        v-b-tooltip.hover
                        tabindex="0">
                        <span class="module_icon ic9-iconMore-0005 ic_more"></span>
                      </b-button>
                    </template>
                    <template v-else>
                      <div class="no_tags">
                        <div class="empty_pattern ace_sec_txt">{{ $t('na') }}</div>
                      </div>
                    </template>
                  </div>
                </div>
              </div>
              <div class="job-meta-row job-meta-align" v-if="embedded">
                <div class="job-meta-item job-tags-item">
                  <div class="icon_container">
                    <span class="ic22-tag2"></span>
                  </div>
                  <div class="details ace_sec_txt">
                    <template v-if="hasJobTags">
                      <div class="tags_container">
                        <div class="tags_cat">
                          <div class="tag" v-for="item in visibleJobTags" :key="item.key" :class="item.cssClass" :aria-label="item.name" tabindex="0" role="button" @click.stop="onTagClick(item)" @keydown.enter.prevent="onTagClick(item)">
                            <span  class="tag_name" :class="item.nameClass" :title="item.name">{{item.name}}</span>
                            <span class="tag_lavel" v-if="item.level">{{item.level}}</span>
                          </div>
                        </div>
                      </div>
                      <b-button class="btn ic_btn btn_ellipsis"
                        v-if="hasMoreJobTags"
                        @click="showTagsPopup"
                        :aria-label="$t('showAllTags')"
                        :title="$t('showAllTags')"
                        v-b-tooltip.hover
                        tabindex="0">
                        <span class="module_icon ic9-iconMore-0005 ic_more"></span>
                      </b-button>
                    </template>
                    <template v-else>
                      <div class="no_tags">
                        <div class="empty_pattern ace_sec_txt">{{ $t('na') }}</div>
                      </div>
                    </template>
                  </div>
                </div>
                <div class="job-meta-item">
                  <div class="icon_container">
                    <span class="icon-location"></span>
                  </div>
                  <div class="ace_sec_txt">
                    {{ jobLocation }}
                  </div>
                </div>
              </div>
              <div class="job-meta-row job-meta-align">
                <div class="job-meta-item">
                  <div class="icon_container">
                    <span class="ic22-eye"></span>
                  </div>
                  <div class="ace_sec_txt">
                    {{ job.viewCount }} views
                  </div>
                </div>
                <div class="job-meta-item">
                  <div class="icon_container">
                    <span class="ic2_job-application"></span>
                  </div>
                  <div class="ace_sec_txt">
                    {{applicantCountMessage(job)}}
                  </div>
                </div>
              </div>

            </div>
            <div class="contact-info-logo" v-if="employerLogo">
              <img @click.stop="selectEmployer(job)" :src="employerLogo" alt=""/>
            </div>
          </div>

          </div>
          <div class="job-meta-row description_row ace_sec_txt">
            <div class="job-meta-item job-description-item">
              <div class="icon_container">
                <span class="ic ic2_description"></span>
              </div>
              <div>
                <h1 class="head_txt module_type_name ace_sec_hd" role="heading" aria-level="1">
                  {{ $t('jobDescription') }} :
                </h1>
              </div>
            </div>
          </div>
          <div class="job-meta-row description_row ace_sec_txt">
            <div class="job-meta-item job-description-item">
              <div class="job-description" v-if="job.description" v-html="job.description"></div>
              <div v-else class="no-description">{{ $t('noDescriptionForJob') }}</div>
            </div>
          </div>
          <template v-if="job.jobCategory">
          <div class="job-meta-row description_row ace_sec_txt">
            <div class="job-meta-item job-description-item">
              <div class="icon_container">
                <span class="icon-paragraph-left"></span>
              </div>
              <div>
                <h1 class="head_txt module_type_name ace_sec_hd" role="heading" aria-level="1">
                  {{ $t('jobCategory') }} :
                </h1>
              </div>
            </div>
          </div>
          <div class="job-meta-row description_row ace_sec_txt">
            <div class="job-meta-item job-description-item">
              <div class="job-description" v-html="job.jobCategory"></div>
            </div>
          </div>
          </template>

          <div class="job-meta-row description_row ace_sec_txt">
            <div class="job-meta-item job-description-item">
              <div class="icon_container">
                <span class="icon-paragraph-left"></span>
              </div>
              <div>
                <h1 class="head_txt module_type_name ace_sec_hd" role="heading" aria-level="1">
                  {{ $t('requirements') }} :
                </h1>
              </div>
            </div>
          </div>
          <div class="job-meta-row description_row ace_sec_txt">
            <div class="job-meta-item job-description-item">
              <div class="job-description" v-if="jobRequirement()" v-html="jobRequirement()"></div>
              <div v-else class="no-description">{{ $t('noRequirementsForJob') }}</div>
            </div>
          </div>
          <template v-if="jobExperience()">
          <div class="job-meta-row description_row ace_sec_txt">
            <div class="job-meta-item job-description-item">
              <div class="icon_container">
                <span class="icon-paragraph-left"></span>
              </div>
              <div>
                <h1 class="head_txt module_type_name ace_sec_hd" role="heading" aria-level="1">
                  {{ $t('experienceRequired') }} :
                </h1>
              </div>
            </div>
          </div>
          <div class="job-meta-row description_row ace_sec_txt">
            <div class="job-meta-item job-description-item">
              <div class="job-description" v-html="jobExperience()"></div>
            </div>
          </div>
          </template>
          <div class="job-meta-row description_row ace_sec_txt">
            <div class="job-meta-item job-description-item">
              <div class="icon_container">
                <span class="ic ic2_description col-left"></span>
              </div>
              <div>
                <h1 class="head_txt module_type_name ace_sec_hd" role="heading" aria-level="1">
                  {{ $t('contactInformation') }} :
                </h1>
              </div>
            </div>
          </div>
          <div class="contact-info-layout">
            <div class="contact-info-meta">
              <div class="job-meta-row job-meta-align contact-meta-align">
                <div class="job-meta-item">
                  <div class="icon_container">
                    <span class="icon-user"></span>
                  </div>
                  <div class="ace_sec_txt">
                    {{ contact }}
                  </div>
                </div>
                <div class="job-meta-item">
                  <div class="icon_container">
                    <span class="ico-employer"></span>
                  </div>
                  <div class="ace_sec_txt">
                    <b-link
                      v-if="job.employerUuid"
                      class="contact-employer-link"
                      :to="{ name: 'employerDetails', params: { uuid: job.employerUuid } }"
                      @click.stop>
                      {{ job.employer }}
                    </b-link>
                    <span v-else>{{ job.employer || $t('na') }}</span>
                  </div>
                </div>
              </div>
              <div class="job-meta-row job-meta-align contact-meta-align">
                <div class="job-meta-item">
                  <div class="icon_container">
                    <span class="icon-phone"></span>
                  </div>
                  <div class="ace_sec_txt">
                    {{ phone }}
                  </div>
                </div>
                <div class="job-meta-item">
                  <div class="icon_container">
                    <span class="ic-inst"></span>
                  </div>
                  <div class="ace_sec_txt" >
                    {{ job.hiringFor?job.hiringFor:jobLocation }}
                  </div>

                </div>
              </div>
              <div class="job-meta-row job-meta-align contact-meta-align">
                <div class="job-meta-item">
                  <div class="icon_container">
                    <span class="ic_email1"></span>
                  </div>
                  <div class="ace_sec_txt">
                    {{ email }}
                  </div>
                </div>
                <div class="job-meta-item">
                  <div class="icon_container">
                    <span class="ic2_url"></span>
                  </div>
                  <div class="ace_sec_txt">
                    <b-link
                      v-if="job.externalLink"
                      :href="job.externalLink"
                      :title="job.externalLink"
                      target="_blank">
                      {{ $t('moreInfo') }}
                    </b-link>
                    <span v-else>{{ $t('na') }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </b-row>
    </b-container>
<!-- Apply Modal -->
    <b-modal ref="jobApplyPopup" :title="$t('applyForThisJob')" class="modal__v22_2" size="lg" hide-header-close no-close-on-backdrop no-close-on-esc centered>
      <div slot="modal-header" class="row header-section w-100">
        <div class="col-11 hd">
          <div class="ic_container">
            <span class="ic2_job-briefcase"></span>
          </div>
          <div class="hd_txt">
            <span>{{ $t('applyForThisJob') }} : {{job.title}}</span>
          </div>
        </div>
        <div class="col-1 btn_container pr-0 mt-0">
          <button class="btn_close" :aria-label="$t('closeAria')" @click="closeApplyPopup">
            <span class="ic-close"></span>
          </button>
        </div>
      </div>
      <div class="details">
        <div class="tags_container">
          You need to apply for job this
          <a :href="job.externalLink" target="_blank">here</a>.
          Once you applied, click <b>mark applied</b> button for tracking application status.</br></br>
          Please make sure your resume, experience and education are <b>visible to employer</b> in your profile.
        </div>
      </div>
      <div slot="modal-footer" class="w-100">
        <div class="btn_container">
          <b-button type="button" class="btn" @click="closeApplyPopup">{{ $t('cancel') }}</b-button>
          <b-button type="button" class="btn" @click="triggerAddJobApplication">{{ $t('markApplied') }}</b-button>
        </div>
      </div>
    </b-modal>
    <b-modal class="modal__v22_2" ref="offerConfirmModal" no-close-on-backdrop no-close-on-esc centered>
      <div slot="modal-header" class="row header-section w-100">
        <div class="col-8 hd">
          <div class="ic_container"></div>
          <div class="hd_txt">
            <span>{{ $t('pleaseConfirm') }}</span>
          </div>
        </div>
        <div class="col-4 btn_container pr-0">
          <button class="btn_close" :aria-label="$t('closeAria')" @click="cancelOfferResponse">
            <span class="ic-close"></span>
          </button>
        </div>
      </div>
      <div class="row modal_msg_container">
        <div class="col-12 msg_sec">
          <span>{{ offerConfirmMessage }}</span>
        </div>
      </div>
      <div slot="modal-footer" class="w-100">
        <b-button @click="confirmOfferResponseYes" class="btn button-primary next float-right mr-2" >{{ $t('yes') }}</b-button>
        <b-button @click="cancelOfferResponse" class="btn button-primary next float-right mr-2" >{{ $t('no') }}</b-button>
      </div>
    </b-modal>
<!-- End Apply Modal -->
<!-- Recommend Modal -->
    <b-modal ref="jobRecommendPopup" :title="$t('recommendThisJob')" class="modal__v22_2 recommend-job-modal" modal-class="recommend-job-modal" content-class="recommend-job-content" body-class="recommend-job-body" size="lg" hide-header-close no-close-on-backdrop no-close-on-esc centered>
      <div slot="modal-header" class="row header-section w-100">
        <div class="col-11 hd">
          <div class="ic_container">
            <span class="ic2_job-briefcase"></span>
          </div>
          <div class="hd_txt">
            <span>{{ $t('recommendThisJob') }} : {{job.title}}</span>
          </div>
        </div>
        <div class="col-1 btn_container pr-0 mt-0">
          <button class="btn_close" :aria-label="$t('closeAria')" @click="closeRecommendPopup">
            <span class="ic-close"></span>
          </button>
        </div>
      </div>
      <div class="details recommend-dialog">
        <div class="recommend-toolbar">
          <div class="toolbar-right">
            <div class="number-of-item ace_sec_txt">
              <b-dropdown id="recommendSizeDropdown" :text="'' + adviseeCount" :aria-label="$t('numberOfItem')">
                <template v-for="(option, index) in adviseeSizeOptions">
                  <b-dropdown-divider v-if="index > 0" :key="'recommend-size-div-' + option"></b-dropdown-divider>
                  <b-dropdown-item :key="'recommend-size-' + option" @click="changeAdviseeSize(option)">{{ option }}</b-dropdown-item>
                </template>
              </b-dropdown>
            </div>
            <div class="pagination btn_container pagination_sty3">
              <button type="button" class="btn btn_trans" :disabled="adviseePage <= 0" @click="prevAdviseePage" @keydown.enter="prevAdviseePage" :title="$t('previousPage')" :aria-label="$t('previousPage')"><span class="ic2_arr_pre3" aria-hidden="true"></span></button>
              <span class="current-page ace_sec_txt">{{ adviseePage + 1 }}</span>
              <button type="button" class="btn btn_trans" :disabled="!hasNextAdviseePage" @click="nextAdviseePage" @keydown.enter="nextAdviseePage" :title="$t('nextPage')" :aria-label="$t('nextPage')"><span class="ic2_arr_next3" aria-hidden="true"></span></button>
            </div>
            <div class="search-box-container">
              <input type="text" class="input-search" :placeholder="$t('typeToNarrowResults')" v-on:keypress.enter="searchAdviseesForRecommend" v-model.trim="adviseeSearch" :aria-label="$t('typeToNarrowResults')">
              <button class="btn btn_srh" @click="searchAdviseesForRecommend" :aria-label="$t('goAria')">
                <span class="ic_srh ic_search" aria-hidden="true"></span>
              </button>
              <button id="recommendSortByDropDown" type="button" @click="toggleRecommendSortDropDown" class="sort-by ace_sec">
                <span class="ic2_sort2 sort-by-icon" aria-hidden="true"></span>
                <span class="txt_srt">{{ $t('sort') }}</span>
              </button>
              <div id="recommendListSortPopup" class="sort_by_popover"></div>
            </div>
            <b-popover target="recommendSortByDropDown" :show.sync="showRecommendSortDropDown" container="recommendListSortPopup" triggers="blur">
              <div class="header-content-row">
                <span class="title">{{ $t('sortBy') }}</span>
              </div>
              <template v-for="sort in adviseeSorts">
                <div class="field-row" :key="sort.name">
                  <div class="form-check">
                    <input type="radio" class="form-check-input" :id="'recommend-sort-' + sort.name" v-model="selectedAdviseeSortName" :value="sort.name">
                    <label class="form-check-label" :for="'recommend-sort-' + sort.name">{{ sort.name }}</label>
                  </div>
                </div>
              </template>
              <b-btn @click="recommendSortOkClicked" class="range-button">{{ $t('ok') }}</b-btn>
            </b-popover>
          </div>
        </div>
        <div class="recommend-user-list">
          <div v-if="advisees.length === 0" class="msg_no_result ace_sec_txt">
            {{ $t('noResult') }}
          </div>
          <label
            class="recommend-user-row"
            v-for="advisee in advisees"
            :key="advisee.uuid">
            <input type="checkbox" :value="advisee.uuid" v-model="selectedAdviseeUuids">
            <div class="recommend-user-info">
              <div class="recommend-user-name ace_sec_txt">{{ adviseeDisplayName(advisee) }}</div>
              <div class="recommend-user-email ace_sec_txt" v-if="advisee.eMail">{{ advisee.eMail }}</div>
            </div>
          </label>
        </div>
      </div>
      <div slot="modal-footer" class="w-100">
        <div class="btn_container">
          <b-button type="button" class="btn" @click="closeRecommendPopup">{{ $t('cancel') }}</b-button>
          <b-button type="button" class="btn" :disabled="selectedAdviseeUuids.length === 0" @click="triggerRecommendJob">{{ $t('recommend') }}</b-button>
        </div>
      </div>
    </b-modal>
<!-- End Recommend Modal -->
    <b-modal ref="jobHomeTagsPopup" :title="$t('tags')" class="modal__v22_2" size="lg" hide-header-close no-close-on-backdrop no-close-on-esc centered>
      <div slot="modal-header" class="row header-section w-100">
        <div class="col-11 hd">
          <div class="ic_container">
            <span class="ic ic-tag"></span>
          </div>
          <div class="hd_txt">
            <span>{{ $t('tags') }}</span>
          </div>
        </div>
        <div class="col-1 btn_container pr-0 mt-0">
          <button class="btn_close" :aria-label="$t('closeAria')" @click="closeTagsPopup">
            <span class="ic-close"></span>
          </button>
        </div>
      </div>
      <div class="details">
        <div class="tags_container">
          <div class="tag" v-for="item in jobTags" :key="item.key" :class="item.cssClass" :aria-label="item.name" tabindex="0" role="button" @click.stop="onTagClick(item)" @keydown.enter.prevent="onTagClick(item)">
            <span class="tag_name" :class="item.nameClass" :title="item.name">{{item.name}}</span>
            <span class="tag_lavel" v-if="item.level">{{item.level}}</span>
          </div>
        </div>
      </div>
      <div slot="modal-footer" class="w-100">
        <div class="btn_container">
          <b-button type="button" class="btn" @click="closeTagsPopup">{{ $t('ok') }}</b-button>
        </div>
      </div>
    </b-modal>

  </div>
</template>

<script>
  import '../../../assets/module/css/job/jobHomepage.min.css'
  import { mapState, mapActions } from 'vuex'
  import { DateTime } from 'luxon'
  import JobHeader from './JobHeader'

  export default {
    name: 'jobHome',
    props: {
      uuid: {
        type: String,
        default: ''
      },
      embedded: {
        type: Boolean,
        default: false
      }
    },
    components: {
      JobHeader
    },
    data: () => {
      return {
        userApplication : {},
        userRecommendations: [],
        advisees: [],
        selectedAdviseeUuids: [],
        adviseeSearch: '',
        adviseePage: 0,
        adviseeCount: 10,
        adviseeTotal: 0,
        adviseeSizeOptions: [10, 50, 100],
        showRecommendSortDropDown: false,
        selectedAdviseeSortName: 'Name',
        adviseeSorts: [
          { name: 'Name', value: 'firstName' },
          { name: 'Last Name', value: 'lastName' },
          { name: 'Email', value: 'eMail' }
        ],
        statusStages: [
          { key: 'appliedOn', label: 'Applied', status: 'Applied' },
          { key: 'reviewedOn', label: 'Reviewed', status: 'Reviewed' },
          { key: 'interviewScheduledOn', label: 'Interview', status: 'Interview Scheduled' },
          { key: 'interviewedOn', label: 'Interviewed', status: 'Interview Done' },
          { key: 'offeredOn', label: 'Job Offer', status: 'Job Offered' },
          { key: 'acceptedOn', label: 'Job Acceptance', status: 'Job Accepted' }
        ],
        offerResponseSaving: false,
        pendingOfferAction: ''
      }
    },
    mounted () {
      this.loadJob()
      this.loadUserApplication()
      this.loadRecommendationForUser()
    },

    methods: {
      ...mapActions([
        'loadJobDetail',
        'loadLogActivity',
        'addJobActivity',
        'addJobApplication',
        'getJobApplication',
        'updateJobApplicationUserStatus',
        'loadRecommendationForEntity',
        'loadAllAdviseesMinInfo',
        'recommendToUsers'
      ]),

      loadJob () {
        this.loadJobDetail(this.uuid).then(res => {
          var uObj = JSON.parse(localStorage.getItem('userobj'))
          var role = localStorage.getItem('userrole')
          var institution = localStorage.getItem('institution')
          var category = ''
          if (!this.job.jobCategory) {
            category = 'Miscellaneous'
          } else {
            category = this.job.jobCategory
          }
          var data = 'jobUuid=' + this.job.uuid +
                     '&jobTitle=' + this.job.title +
                     '&jobCategory=' + category +
                     '&action=viewJob' +
                     '&productArea=Employer' +
                     '&userUuid=' + uObj.uuid +
                     '&userRole=' + role +
                     '&institutionName=' + institution +
                     '&institutionUuid=' + uObj.institutionId +
                     '&studentType=' +
                     '&advisor=' +
                     '&major=' + uObj.major1 +
                     '&concentration=' +
                     '&minor=' +
                     '&activity=jobHome' +
                     '&product=' +
                     '&productPackage=' +
                     '&page=jobHome' +
                     '&MajorCIP=' +
                     '&GPA=' + uObj.majorGpa +
                     '&MajorGPA=' + uObj.overallGpa

          this.loadLogActivity({data: data})
          this.triggerAddJobActivity(uObj, this.job)
        })
      },
      selectEmployer (result){
          return this.$router.push({ name: 'employerDetails', params: { uuid: result.employerUuid } })
      },
      triggerAddJobActivity (){
        let aUser = JSON.parse(localStorage.getItem('userobj'))
        let jobActivity = {
              jobId : this.job.uuid,
              jobTitle: this.job.title,
              employerId: this.job.employerUuid,
              employerTitle: this.job.employer,
              institutionId: this.institution.uuid,
              institutionTitle: this.institution.title,
              userId :aUser.uuid,
              userFullName: aUser.firstName + ' ' +aUser.lastName,
              type: 'View'
        }
        this.addJobActivity(jobActivity)
      },
      applicantCountMessage (result){
          let count = result.applicantCount || 0
          return (count<2)? ''+ count +' applicant':''+ count +' applicants'
      },
      triggerAddJobApplication (){
        if(!this.isStudentLogin){
            alert('This action is for the applicants.')
            this.closeApplyPopup()
            return
        }
        let aUser = JSON.parse(localStorage.getItem('userobj'))
        let jobApplication = {
              jobId : this.job.uuid,
              jobTitle: this.job.title,
              employerId: this.job.employerUuid,
              employerTitle: this.job.employer,
              employerLogo: this.job.thumbnail || this.job.thumbnailURI,
              institutionId: this.institution.uuid,
              institutionTitle: this.institution.title,
              institutionLogo: this.institution.logo,
              userId :aUser.uuid,
              userFullName: aUser.firstName + ' ' +aUser.lastName,
              status: 'Applied'
        }
        this.addJobApplication(jobApplication).then(res => {
          if (res && res.status) {
            this.userApplication = res
          } else {
            this.userApplication = jobApplication
          }
          this.closeApplyPopup()
        })
      },
      loadUserApplication () {
        this.userApplication = {}
        if (!this.uuid) {
          return
        }
        this.getJobApplication(this.uuid).then(res => {
          if (res && res.status) {
            this.userApplication = res
          } else {
            this.userApplication = {}
          }
        })
      },
      loadRecommendationForUser () {
        this.userRecommendations = []
        if (!this.uuid) {
          return
        }
        this.loadRecommendationForEntity(this.uuid).then(res => {
          let recs = res
          if (res && res.data && (Array.isArray(res.data) || res.data.type || res.data.recommendedBy)) {
            recs = res.data
          }
          if (!Array.isArray(recs)) {
            recs = recs ? [recs] : []
          }
          this.userRecommendations = recs.filter(rec => rec && (rec.type || rec.recommendedBy))
        })
      },
      jobRequirement(){
        return this.job.educationQualification && this.job.educationQualification.length>0?this.job.educationQualification[0]:''
      },
      jobExperience(){
        return this.job.experienceRequired && this.job.experienceRequired.length>0?this.job.experienceRequired[0]:''
      },


      millisToDate (millis) {
        if (!millis) {
          return this.$t('untilFilled')
        }
        let parsed = this.parseDateTime(millis)
        return parsed && parsed.isValid ? parsed.toFormat('MM/dd/yyyy') : 'N/A'
      },
      parseDateTime (value) {
        if (value === undefined || value === null || value === '') {
          return null
        }
        if (typeof value === 'number') {
          return DateTime.fromMillis(value < 1e12 ? value * 1000 : value)
        }
        if (value instanceof Date) {
          return DateTime.fromJSDate(value)
        }
        if (typeof value === 'object') {
          return this.parseDateTime(value.epochMillis || value.millis || value.time || value.epochSecond)
        }
        let text = String(value).trim()
        if (!text) {
          return null
        }
        let parsed = DateTime.fromISO(text)
        if (parsed.isValid) {
          return parsed
        }
        parsed = DateTime.fromFormat(text, 'MM/dd/yyyy')
        if (parsed.isValid) {
          return parsed
        }
        parsed = DateTime.fromFormat(text, 'yyyy-MM-dd HH:mm:ss')
        if (parsed.isValid) {
          return parsed
        }
        let asNumber = Number(text)
        if (!isNaN(asNumber)) {
          return this.parseDateTime(asNumber)
        }
        return parsed
      },
      applicationDate (application) {
        if (!application) {
          return null
        }
        return application.creationTime || application.appliedTime || application.statusUpdatedTime || application.lastUpdatedTime
      },
      stageDateValue (application, stage) {
        if (!application || !stage) {
          return null
        }
        if (stage.key === 'acceptedOn' && this.isOfferDeclined(application)) {
          return application.declinedOn || application.statusUpdatedTime || application.lastUpdatedTime
        }
        let value = application[stage.key]
        if (stage.key === 'appliedOn' && !value) {
          value = this.applicationDate(application)
        }
        if (stage.key === 'offeredOn' && !value) {
          value = application.jobOfferedOn || application.offerDate || application.statusUpdatedTime || application.lastUpdatedTime
        }
        return value
      },
      stageDateLabel (application, stage) {
        if (!this.isStageComplete(application, stage) && !this.isStageRejected(application, stage)) {
          return 'N/A'
        }
        return this.millisToDate(this.stageDateValue(application, stage))
      },
      statusRank (status) {
        if (status === 'Accepted') {
          status = 'Job Accepted'
        }
        if (status === 'Offered' || status === 'Job Offer') {
          status = 'Job Offered'
        }
        if (status === 'Declined') {
          return this.statusRank('Job Offered')
        }
        let order = ['Applied', 'Reviewed', 'Interview Scheduled', 'Interview Done', 'Job Offered', 'Job Accepted']
        let index = order.indexOf(status)
        return index < 0 ? 0 : index
      },
      isOfferAccepted (application) {
        if (!application) {
          return false
        }
        return !!(application.acceptedOn || application.status === 'Accepted' || application.status === 'Job Accepted')
      },
      isOfferDeclined (application) {
        if (!application) {
          return false
        }
        return !!(application.declinedOn || application.status === 'Declined' || application.acceptedRejected || application.acceptanceStatus === 'Rejected' || application.acceptanceStatus === 'Declined')
      },
      isStageRejected (application, stage) {
        if (!application || !stage) {
          return false
        }
        if (stage.key === 'interviewScheduledOn') {
          return !!(application.interviewRejected || application.interviewScheduleStatus === 'Rejected')
        }
        if (stage.key === 'offeredOn') {
          return !!(application.offerRejected || application.offerStatus === 'Rejected')
        }
        if (stage.key === 'acceptedOn') {
          return this.isOfferDeclined(application)
        }
        return false
      },
      isStageComplete (application, stage) {
        if (!application || !stage) {
          return false
        }
        if (this.isStageRejected(application, stage)) {
          return false
        }
        if (stage.key === 'acceptedOn') {
          return this.isOfferAccepted(application)
        }
        if (application[stage.key]) {
          return true
        }
        return this.statusRank(application.status) >= this.statusRank(stage.status)
      },
      canRespondToOffer (application, stage) {
        if (!application || !stage || stage.key !== 'acceptedOn') {
          return false
        }
        if (this.isOfferAccepted(application) || this.isOfferDeclined(application)) {
          return false
        }
        return application.status === 'Job Offered' || this.statusRank(application.status) >= this.statusRank('Job Offered')
      },
      stageDisplayLabel (application, stage) {
        if (!stage) {
          return ''
        }
        if (stage.key === 'acceptedOn' && this.isOfferDeclined(application)) {
          return 'Declined'
        }
        if (stage.key === 'acceptedOn' && this.isOfferAccepted(application)) {
          return 'Accepted'
        }
        return stage.label
      },
      confirmOfferResponse (action) {
        this.pendingOfferAction = action
        this.$refs.offerConfirmModal.show()
      },
      cancelOfferResponse () {
        this.pendingOfferAction = ''
        this.$refs.offerConfirmModal.hide()
      },
      confirmOfferResponseYes () {
        let action = this.pendingOfferAction
        this.$refs.offerConfirmModal.hide()
        this.pendingOfferAction = ''
        this.respondToOffer(action)
      },
      respondToOffer (action) {
        let application = this.userApplication
        if (!application || this.offerResponseSaving) {
          return
        }
        let applicationId = application.uuid
        if (!applicationId) {
          return
        }
        let status = action === 'accept' ? 'Accepted' : 'Declined'
        this.offerResponseSaving = true
        this.updateJobApplicationUserStatus({ applicationId: applicationId, status: status }).then((response) => {
          this.offerResponseSaving = false
          let title = application.jobTitle || (this.job && this.job.title) || ''
          if (response) {
            this.applyOfferResponse(response)
            this.notifySuccess('Successfully updated application status for \'' + title + '\'')
          } else {
            this.notifyFailure('Unable to update application status for \'' + title + '\'')
          }
        }).catch(() => {
          this.offerResponseSaving = false
          this.notifyFailure('Unable to update application status for \'' + (application.jobTitle || (this.job && this.job.title) || '') + '\'')
        })
      },
      applyOfferResponse (response) {
        let updated = Object.assign({}, this.userApplication)
        if (response && typeof response === 'object' && !Array.isArray(response)) {
          if (response.data && (response.data.status || response.data.uuid)) {
            updated = Object.assign(updated, response.data)
          } else {
            updated = Object.assign(updated, response)
          }
        }
        this.userApplication = updated
      },
      showTagsPopup () {
        this.$refs.jobHomeTagsPopup.show()
      },
      showApplyPopup() {
        this.$refs.jobApplyPopup.show()
      },
      closeApplyPopup () {
        this.$refs.jobApplyPopup.hide()
      },
      showRecommendPopup() {
        this.adviseeSearch = ''
        this.adviseePage = 0
        this.adviseeCount = 10
        this.selectedAdviseeUuids = []
        this.selectedAdviseeSortName = 'Name'
        this.showRecommendSortDropDown = false
        this.loadAdviseesForRecommend()
        this.$refs.jobRecommendPopup.show()
      },
      closeRecommendPopup () {
        this.$refs.jobRecommendPopup.hide()
      },
      adviseeSortValue () {
        let selected = this.adviseeSorts.find((sort) => {
          return sort.name === this.selectedAdviseeSortName
        })
        return selected ? selected.value : 'firstName'
      },
      adviseeDisplayName (advisee) {
        if (!advisee) {
          return ''
        }
        let name = ((advisee.firstName || '') + ' ' + (advisee.lastName || '')).trim()
        return name || advisee.eMail || advisee.uuid
      },
      loadAdviseesForRecommend () {
        return this.loadAllAdviseesMinInfo({
          name: this.adviseeSearch || '',
          page: this.adviseePage,
          count: this.adviseeCount,
          sort: this.adviseeSortValue()
        }).then(data => {
          let list = []
          let total = 0
          if (Array.isArray(data)) {
            list = data
            total = (this.adviseePage * this.adviseeCount) + data.length
            if (data.length === this.adviseeCount) {
              total = total + 1
            }
          } else if (data) {
            list = data.advisees || data.users || data.content || data.list || data.data || []
            total = data.total || data.totalElements || list.length
          }
          this.advisees = list
          this.adviseeTotal = total
          return list
        })
      },
      searchAdviseesForRecommend () {
        this.adviseePage = 0
        this.loadAdviseesForRecommend()
      },
      changeAdviseeSize (option) {
        this.adviseeCount = option
        this.adviseePage = 0
        this.loadAdviseesForRecommend()
      },
      prevAdviseePage () {
        if (this.adviseePage > 0) {
          this.adviseePage = this.adviseePage - 1
          this.loadAdviseesForRecommend()
        }
      },
      nextAdviseePage () {
        if (this.hasNextAdviseePage) {
          this.adviseePage = this.adviseePage + 1
          this.loadAdviseesForRecommend()
        }
      },
      toggleRecommendSortDropDown () {
        this.showRecommendSortDropDown = !this.showRecommendSortDropDown
      },
      recommendSortOkClicked () {
        this.showRecommendSortDropDown = false
        this.adviseePage = 0
        this.loadAdviseesForRecommend()
      },
      triggerRecommendJob () {
        let title = (this.job && this.job.title) ? this.job.title : ''
        this.recommendToUsers({entityId: this.job.uuid, uuids: this.selectedAdviseeUuids}).then(res => {
          let result = res
          if (res && res.data !== undefined) {
            result = res.data
          }
          if (result === 'Done') {
            this.notifySuccess('Successfully recommended \'' + title + '\'')
            this.closeRecommendPopup()
          } else {
            this.notifyFailure('Unable to recommend \'' + title + '\'')
          }
        }).catch(() => {
          this.notifyFailure('Unable to recommend \'' + title + '\'')
        })
      },
      notifySuccess (text) {
        return this.$notify({
          group: 'notify',
          type: 'success',
          title: '',
          text: text
        })
      },
      notifyFailure (text) {
        return this.$notify({
          group: 'notify',
          type: 'error',
          title: '',
          text: text
        })
      },
      onTagClick (tag) {
        if (!tag || !tag.name) {
          return
        }
        if (this.embedded) {
          this.$emit('filter-by-tag', tag)
          this.closeTagsPopup()
          return
        }
        this.$router.push({
          name: 'jobBoard',
          query: { tag: tag.name }
        })
      },
      closeTagsPopup () {
        if (this.$refs.jobHomeTagsPopup) {
          this.$refs.jobHomeTagsPopup.hide()
        }
      },
      formatJobTypeLabel (value) {
        if (!value) {
          return value
        }
        let text = String(value).replace(/[_-]+/g, ' ')
        text = text.replace(/([a-z])([A-Z])/g, '$1 $2')
        text = text.replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
        let compact = text.replace(/\s+/g, '').toLowerCase()
        let known = {
          fulltime: 'Full Time',
          parttime: 'Part Time'
        }
        if (known[compact]) {
          return known[compact]
        }
        return text.replace(/\s+/g, ' ').trim()
      }
    },
    computed: {
      ...mapState({
        'job': state => state.job.job,
        'institution' : state => state.institution.institution
      }),
      jobTypeLabel () {
        if (this.job && this.job.jobTypes && this.job.jobTypes.length > 0) {
          return this.job.jobTypes[0]
        }
        return 'N/A'
      },
      isStudentLogin () {
        return (localStorage.getItem('userrole') === 'Student')
      },
      isAdvisorLogin () {
        return (localStorage.getItem('userrole') === 'Advisor')
      },
      hasNextAdviseePage () {
        return ((this.adviseePage + 1) * this.adviseeCount) < this.adviseeTotal
      },
      jobLocation () {
        if (this.job && this.job.contactInfo && this.job.contactInfo.addresses && this.job.contactInfo.addresses.length > 0) {
          let address = this.job.contactInfo.addresses[0]
          let city = address.city || ''
          let state = address.state || ''
          if (city && state) {
            return city + ', ' + state
          }
          return city || state || 'N/A'
        }
        return 'N/A'
      },

      jobTags () {
        let tags = []
        if (!this.job) {
          return tags
        }
        let appliedTagMap = this.job.appliedTagMap
        if (appliedTagMap) {
          Object.keys(appliedTagMap).forEach((key) => {
            let values = appliedTagMap[key]
            if (values && values.length) {
              for (let i = 0; i < values.length; i++) {
                let tag = values[i]
                tags.push({
                  key: 'applied-' + (tag.uuid || tag.name || i),
                  name: tag.name,
                  category: key,
                  kind: 'applied',
                  nameClass: 'instructional_tag',
                  cssClass: '',
                  level: ''
                })
              }
            }
          })
        }
        let competencies = this.job.careerCompetenciesList
        if (competencies && competencies.length) {
          for (let i = 0; i < competencies.length; i++) {
            let tag = competencies[i]
            let level = ''
            if (tag.fulfillLevels && tag.fulfillLevels.length > 0) {
              level = tag.fulfillLevels[0]
            }
            tags.push({
              key: 'competency-' + (tag.uuid || tag.name || i),
              name: tag.name,
              category: tag.tagType || '',
              kind: 'competency',
              nameClass: 'competency_tag',
              cssClass: tag.tagType || '',
              level: level
            })
          }
        }
        return tags
      },
      visibleJobTags () {
        let max = this.embedded?1:3
        return this.jobTags.slice(0, max)
      },
      hasMoreJobTags () {
        let max = this.embedded?1:3
        return this.jobTags.length > max
      },
      hasJobTags () {
        return this.jobTags.length > 0
      },
      contact () {
        if (this.job.contactInfo && this.job.contactInfo.contactsList && this.job.contactInfo.contactsList.length > 0) {
          var contact = ''
          if (this.job.contactInfo.contactsList[0].name) {
            contact = this.job.contactInfo.contactsList[0].name 
          }

          if (this.job.contactInfo.contactsList[0].title) {
            contact = contact + ', '+ this.job.contactInfo.contactsList[0].title
          }
          return contact
        } else {
          return 'N/A'
        }
      },
      phone () {
        if (this.job.contactInfo && this.job.contactInfo.phoneList && this.job.contactInfo.phoneList.length > 0) {
          var phone = this.job.contactInfo.phoneList[0].value
          return phone
        } else {
          return 'N/A'
        }
      },
      email () {
        if (this.job.contactInfo && this.job.contactInfo.eMailList && this.job.contactInfo.eMailList.length > 0) {
          var email = this.job.contactInfo.eMailList[0].value
          return email
        } else {
          return 'N/A'
        }
      },
      address () {
        if (this.job.contactInfo && this.job.contactInfo.addresses && this.job.contactInfo.addresses.length > 0) {
          var address = this.job.contactInfo.addresses[0].city + ' ' + this.job.contactInfo.addresses[0].state + ' ' + this.job.contactInfo.addresses[0].country
          return address
        } else {
          return 'N/A'
        }
      },
      hasApplication () {
        return !!(this.userApplication && this.userApplication.status)
      },
      offerConfirmMessage () {
        if (this.pendingOfferAction === 'decline') {
          return 'Are you sure you want to decline this offer?'
        }
        return 'Are you sure you want to accept this offer?'
      },
      hasRecommendation () {
        return this.userRecommendations && this.userRecommendations.length > 0
      },

      appliedMessage () {
        if (!this.hasApplication || !this.userApplication) {
          return ''
        }
        return 'You ' + this.userApplication.status + ' for this job on ' + this.millisToDate(this.userApplication.appliedOn)
      },
      employerLogo () {
        if (!this.job) {
          return ''
        }
        return this.job.thumbnail || this.job.thumbnailURI || ''
      }
    },
    watch: {
      uuid () {
        this.loadUserApplication()
        this.loadRecommendationForUser()
      }
    }
  }
</script>

<style scoped>
    .margin-top-10 {
      margin-top: 10px;
    }
    .margin-top-20 {
      margin-top: 20px;
    }
    .margin-top-30 {
      margin-top: 30px;
    }
    .no-border {
      border: none;
    }
    .margin-left-20 {
      margin-left: 20px;
    }

    .page-content-section .left-container {
      float: none;
      width: 100%;
      max-width: 100%;
      margin-top: 0;
      margin-left:30px;
    }

    .job-description {
      flex: 1;
      min-width: 0;
      width: auto;
      display: block;
      white-space: pre-wrap;
      word-wrap: break-word;
      margin-top: 0;
      padding-left: 2.5em;
      line-height: 1.5;
    }
    .app-status{
        margin-top: 0px;
    }
    .job-application-status {
      display: flex;
      align-items: stretch;
      width: 98%;
      max-width: 98%;
      box-sizing: border-box;
      padding-left: 2.5em;
      min-width: 0;
      margin-bottom: 30px;
    }

    .job-status-box {
      flex: 1 1 0;
      min-width: 0;
      padding: 6px 4px;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      text-align: center;
      border-radius: 10px;
      background: #c4c4c4;
      color: #000;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.16);
    }

    .job-status-box.is-complete {
      background: #c8e6c0;
    }

    .job-status-box.is-rejected {
      background: #c97a7a;
    }

    .job-status-box.has-offer-actions {
      padding: 4px 3px;
    }

    .offer-actions {
      display: grid;
      grid-auto-flow: column;
      grid-auto-columns: 1fr;
      align-items: center;
      justify-content: center;
      width: max-content;
      max-width: 100%;
      gap: 8px;
    }

    .offer-btn {
      border: none;
      border-radius: 8px;
      padding: 7px 14px;
      font-size: 12px;
      font-weight: 600;
      line-height: 1.2;
      cursor: pointer;
      width: 100%;
      white-space: nowrap;
    }

    .offer-btn.accept {
      background: #4caf50;
      color: #fff;
    }

    .offer-btn.decline {
      background: #e53935;
      color: #fff;
    }

    .job-application-status.is-embedded .offer-actions {
      grid-auto-flow: row;
      width: 100%;
      max-width: 100%;
      gap: 4px;
    }

    .job-application-status.is-embedded .offer-btn {
      padding: 3px 6px;
      font-size: 10px;
      border-radius: 6px;
    }

    .job-status-label {
      font-size: 11px;
      font-weight: 400;
      line-height: 1.2;
      color: #000;
      white-space: nowrap;
    }

    .job-status-date {
      margin-top: 3px;
      font-size: 10px;
      font-weight: 400;
      line-height: 1.2;
      color: #000;
      white-space: nowrap;
    }

    .job-status-separator {
      flex: 0 0 auto;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 0 10px;
      color: #8a8a8a;
      font-size: 16px;
      font-weight: 600;
      line-height: 1;
    }

    .job-summary-header {
      display: flex;
      align-items: flex-start;
      width: 100%;
      min-width: 0;
    }

    .job-summary-meta {
      flex: 1 1 auto;
      min-width: 0;
    }

    .contact-info-logo {
      flex: 0 0 auto;
      margin: 0 0 0 24px;
      align-self: flex-start;
      cursor: pointer;
    }

    .contact-info-layout {
      width: 100%;
      min-width: 0;
      margin-top: 20px;
      margin-left: 30px;
    }

    .contact-info-meta {
      width: 100%;
      min-width: 0;
    }

    .contact-info-logo img {
      max-height: 80px;
      max-width: 240px;
      width: auto;
      height: auto;
      cursor: pointer;
    }

    .contact-employer-link {

    }

    .contact-employer-link:hover,
    .contact-employer-link:focus {

    }

    .job-description >>> * {
      line-height: 1.5;
    }

    .no-description {
      flex: 1;
      min-width: 0;
      padding-left: 4em;
      line-height: 1.5;
    }

    .description_row {
      min-height: 24px;
      margin-top: 16px;
      margin-bottom: 0;
    }

    .job-summary {
      width: 100%;
    }

    .job-summary.is-embedded .job-meta-row.job-meta-align {
      grid-template-columns: minmax(240px, 1.4fr) minmax(160px, 1fr);
    }

    .job-summary.is-embedded .job-meta-row.job-meta-align .job-meta-item:first-child .ace_sec_txt {
      white-space: nowrap;
    }

    .job-summary .job-salary-item {
      min-width: 0;
      overflow: hidden;
      max-width: 100%;
    }

    .job-summary .job-salary-item .ace_sec_txt {
      display: block;
      flex: 1 1 0%;
      width: 0;
      min-width: 0;
      max-width: 100%;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }









    .recommendation-row {
      background: #fff8dc;
      padding: 6px 12px;
      border-radius: 6px;
      box-sizing: border-box;
      width: 100%;
      margin-right: 0;
    }

    .recommendation-row .job-description-item {
      flex-direction: column;
      align-items: stretch;
    }

    .recommendation-item {
      display: flex;
      align-items: center;
      width: 100%;
    }

    .recommendation-item + .recommendation-item {
      margin-top: 6px;
    }

    .job-tags-item {
      flex: 1;
      min-width: 0;
      margin-right: 0;
    }

    .job-tags-item .details {
      display: flex;
      align-items: center;
      flex: 1;
      min-width: 0;
      float: none;
      width: auto;
      max-width: none;
      height: auto;
      overflow: hidden;
    }

    .job-tags-item .tags_container,
    .job-tags-item .tags_cat {
      display: flex;
      align-items: center;
      flex-wrap: nowrap;
      float: none;
      width: auto;
      max-width: none;
      height: auto;
      margin: 0;
      padding: 0;
      overflow: hidden;
    }

    .job-tags-item .tags_container {
      flex: 0 1 auto;
      min-width: 0;
    }

    .job-tags-item .tag {
      flex-shrink: 0;
      white-space: nowrap;
      width: auto;
      margin: 0 8px 0 0;
      cursor: pointer;
    }

    .job-tags-item .tag .tag_name {
      overflow: visible;
      text-overflow: clip;
      width: auto;
      max-width: none;
    }

    .job-tags-item .no_tags .empty_pattern {
      padding: 0;
    }
    .job-tags-item .btn_ellipsis {
      float: none;
      margin-top: 0;
      margin-left: 4px;
      flex-shrink: 0;
    }

    .ic-inst {
      font-size: 28px !important;
      line-height: 1;
      margin: 0;
      display: block;
    }

    .recommend-dialog {
      padding: 0;
      overflow: hidden;
      display: flex;
      flex-direction: column;
      min-height: 0;
    }

    .recommend-toolbar {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      flex-wrap: wrap;
      width: 100%;
      padding: 0 0 10px;
      gap: 8px;
      flex-shrink: 0;
    }

    .recommend-toolbar .toolbar-left,
    .recommend-toolbar .toolbar-right {
      display: flex;
      align-items: center;
      flex-wrap: nowrap;
    }

    .recommend-toolbar .out-of-total-item {
      margin-right: 10px;
      white-space: nowrap;
    }

    .recommend-toolbar .number-of-item {
      margin-right: 10px;
    }

    .recommend-toolbar .pagination {
      display: flex;
      align-items: center;
      margin-right: 12px;
      margin-top: 0;
      padding-top: 0;
    }



    .recommend-toolbar .search-box-container {
      display: flex;
      align-items: center;
    }

    .recommend-user-list {
      flex: 1 1 auto;
      min-height: 0;
      max-height: calc(100vh - 280px);
      overflow-y: auto;
      border-top: 1px solid #ebebeb;
    }

    .recommend-user-row {
      display: flex;
      align-items: center;
      width: 100%;
      margin: 0;
      padding: 10px 8px;
      border-bottom: 1px solid #f0f0f0;
      cursor: pointer;
    }

    .recommend-user-row input {
      margin: 0 10px 0 0;
      flex-shrink: 0;
    }

    .recommend-user-info {
      min-width: 0;
    }

    .recommend-user-name {
      font-size: 15px;
      font-weight: 500;
      line-height: 1.3;
    }

    .recommend-user-email {
      font-size: 12px;
      line-height: 1.3;
      color: #666;
    }

    .recommend-user-list .msg_no_result {
      padding: 24px 8px;
      text-align: center;
    }
</style>

<style>
  .modal-dialog.recommend-job-modal {
    max-height: calc(100vh - 32px);
    margin-top: 16px;
    margin-bottom: 16px;
  }

  .recommend-job-content {
    max-height: calc(100vh - 32px);
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }

  .recommend-job-body {
    overflow: hidden !important;
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }

  .recommend-job-body .recommend-dialog {
    flex: 1 1 auto;
    min-height: 0;
    overflow: hidden;
  }

  .recommend-job-modal .sort_by_popover .popover {
    min-width: 200px;
  }
</style>



// WEBPACK FOOTER //
// src/app/job/components/JobHome.vue